// ========================================
// 📄 src/services/exportService.js
// ========================================
import { formatDate, formatDateTime } from "../utils/formatters";

export const exportService = {
  // Export des leads en PDF
  async exportLeadsPDF(leads) {
    // Pour une implémentation complète, vous pourriez utiliser jsPDF
    // Pour l'instant, nous créons un CSV formaté qui peut être ouvert dans Excel

    const headers = [
      "Prénom",
      "Nom",
      "Email",
      "Téléphone",
      "Entreprise",
      "Fonction",
      "Statut",
      "Source",
      "Date de prospection",
      "Produit d'intérêt",
      "Date de création",
    ];

    const rows = leads.map((lead) => [
      lead.firstName || "",
      lead.lastName || "",
      lead.email || "",
      lead.phone || "",
      lead.company || "",
      lead.jobTitle || "",
      lead.status || "",
      lead.source || "",
      formatDate(lead.prospectionDate),
      lead.productInterest || "",
      lead.createdAt ? formatDateTime(lead.createdAt) : "",
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    this.downloadFile(csvContent, "prospects.csv", "text/csv");
  },

  // Export des leads en Excel
  async exportLeadsExcel(leads) {
    // Pour une implémentation complète, vous pourriez utiliser SheetJS
    // Pour l'instant, nous créons un CSV qui peut être ouvert dans Excel

    const headers = [
      "Prénom",
      "Nom",
      "Email",
      "Téléphone",
      "Entreprise",
      "Fonction",
      "Statut",
      "Source",
      "Date de prospection",
      "Produit d'intérêt",
      "Date de création",
    ];

    const rows = leads.map((lead) => [
      lead.firstName || "",
      lead.lastName || "",
      lead.email || "",
      lead.phone || "",
      lead.company || "",
      lead.jobTitle || "",
      lead.status || "",
      lead.source || "",
      formatDate(lead.prospectionDate),
      lead.productInterest || "",
      lead.createdAt ? formatDateTime(lead.createdAt) : "",
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    this.downloadFile(
      csvContent,
      "prospects.xlsx",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    );
  },

  // Export des leads en CSV
  async exportLeadsCSV(leads) {
    const headers = [
      "Prénom",
      "Nom",
      "Email",
      "Téléphone",
      "Entreprise",
      "Fonction",
      "Statut",
      "Source",
      "Date de prospection",
      "Produit d'intérêt",
      "Date de création",
    ];

    const rows = leads.map((lead) => [
      lead.firstName || "",
      lead.lastName || "",
      lead.email || "",
      lead.phone || "",
      lead.company || "",
      lead.jobTitle || "",
      lead.status || "",
      lead.source || "",
      formatDate(lead.prospectionDate),
      lead.productInterest || "",
      lead.createdAt ? formatDateTime(lead.createdAt) : "",
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    this.downloadFile(csvContent, "prospects.csv", "text/csv");
  },

  // Export principal - route vers le bon format
  async exportLeads(leads, format = "pdf") {
    switch (format) {
      case "pdf":
        return await this.exportLeadsPDF(leads);
      case "excel":
        return await this.exportLeadsExcel(leads);
      case "csv":
        return await this.exportLeadsCSV(leads);
      default:
        throw new Error(`Format d'export non supporté: ${format}`);
    }
  },

  // Export des statistiques
  async exportStats(stats, format = "pdf") {
    const statsData = {
      totalLeads: stats.total || 0,
      nouveauxLeads: stats.nouveau || 0,
      leadsContactes: stats.contacte || 0,
      leadsQualifies: stats.qualifie || 0,
      leadsEnNegociation: stats.negociation || 0,
      leadsConvertis: stats.converti || 0,
      leadsPerdus: stats.perdu || 0,
      tauxConversion: stats.conversionRate || 0,
      dateExport: new Date().toLocaleDateString("fr-FR"),
    };

    const csvContent = Object.entries(statsData)
      .map(([key, value]) => `"${key}","${value}"`)
      .join("\n");

    this.downloadFile(csvContent, `statistiques-${format}.csv`, "text/csv");
  },

  // Fonction utilitaire pour télécharger un fichier
  downloadFile(content, filename, mimeType) {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  // Export d'un rapport complet
  async exportFullReport(leads, stats, interactions = []) {
    const reportData = {
      metadata: {
        dateExport: new Date().toLocaleDateString("fr-FR"),
        heureExport: new Date().toLocaleTimeString("fr-FR"),
        totalLeads: leads.length,
        totalInteractions: interactions.length,
      },
      stats: stats,
      leads: leads,
      interactions: interactions,
    };

    const reportContent = JSON.stringify(reportData, null, 2);
    this.downloadFile(
      reportContent,
      "rapport-complet.json",
      "application/json"
    );
  },
};
